import { CommonModule } from '@angular/common'
import { Component, Input } from '@angular/core'
import { animationFadeInOut } from '@engineering11/ui-lib/e11-animations'

export type LoaderLinkSize = 'sm' | 'md' | 'lg' | 'xl'

interface LinkLoopDimensions {
  /** Rendered width/height of the mark, in pixels. */
  px: number
  /** Stroke width in viewBox units - heavier at small sizes so the links stay legible. */
  stroke: number
}

/** Legacy `color` values that mean "draw this on a dark surface". */
const WHITE_COLORS = ['e11-bg-skin-white', 'e11-bg-white', 'white', '#fff', '#ffffff']

const DIMENSIONS: Record<LoaderLinkSize, LinkLoopDimensions> = {
  sm: { px: 22, stroke: 7 },
  md: { px: 44, stroke: 6 },
  lg: { px: 68, stroke: 5 },
  xl: { px: 96, stroke: 4.5 },
}

/**
 * The Cnect "Link Loop" loader - the two interlocking links of the brand mark, counter-rotating
 * while work is in flight.
 *
 * Colors come from the active theme, so a tenant with its own theme gets its own two colors with
 * no change here: the left link is the theme's primary accent and the right link is the theme's
 * secondary color (blue and pink for Cnect).
 *
 * Drop-in replacement for `e11-loader-dots` - it takes the same inputs.
 */
@Component({
  selector: 'cnect-loader-link',
  standalone: true,
  imports: [CommonModule],
  animations: [animationFadeInOut()],
  template: `
    <div
      *ngIf="active"
      class="cnect-loader-link e11-z-50"
      [ngClass]="{ 'cnect-loader-link-fullscreen': fullScreen }"
      [@animationFadeInOut]
    >
      <div
        class="cnect-loader-link-inner e11-z-50 e11-w-full e11-text-center"
        [ngClass]="{ 'cnect-loader-link-button': buttonLoader, 'cnect-loader-link-fade': fade }"
        role="status"
        aria-live="polite"
        [attr.aria-label]="label || 'Loading'"
      >
        <svg
          class="cnect-loader-link-mark"
          [class.cnect-loader-link-inverted]="inverted"
          [attr.width]="dimensions.px"
          [attr.height]="dimensions.px"
          viewBox="0 0 64 64"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            class="cnect-loader-link-ring cnect-loader-link-ring-a"
            cx="25"
            cy="32"
            r="13"
            fill="none"
            stroke-linecap="round"
            stroke-dasharray="56 26"
            [attr.stroke-width]="dimensions.stroke"
          ></circle>
          <circle
            class="cnect-loader-link-ring cnect-loader-link-ring-b"
            cx="39"
            cy="32"
            r="13"
            fill="none"
            stroke-linecap="round"
            stroke-dasharray="56 26"
            [attr.stroke-width]="dimensions.stroke"
          ></circle>
        </svg>
        <h3
          class="cnect-loader-link-title e11-relative e11-text-center e11-my-4 {{ fontColor }}"
          [style.fontSize]="fontSize"
          *ngIf="!buttonLoader && label"
        >
          {{ label }}
        </h3>
      </div>
    </div>

    <div
      *ngIf="active && fullScreen && hasBackdrop"
      class="e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm"
      [ngClass]="{ 'e11-bg-black/70': backdropStyle === 'dark', 'e11-bg-white/70': backdropStyle === 'light' }"
      [@animationFadeInOut]
    ></div>
  `,
  styles: [
    `
      .cnect-loader-link.cnect-loader-link-fullscreen {
        position: fixed;
        display: flex;
        inset: 0;
        align-items: center;
        justify-content: center;
      }
      .cnect-loader-link .cnect-loader-link-fade {
        opacity: 0;
        z-index: -2;
        transition: opacity 1s ease-in-out 0s, z-index 0s ease-in-out 1.2s;
      }

      /* Theme colors - a tenant theme redefines these variables and the loader follows. */
      .cnect-loader-link-ring-a {
        stroke: rgb(var(--color-primary-accent));
      }
      .cnect-loader-link-ring-b {
        stroke: rgb(var(--color-secondary));
      }
      /* For loaders sitting on a dark surface or backdrop. */
      .cnect-loader-link-inverted .cnect-loader-link-ring-a {
        stroke: rgb(var(--color-white));
      }
      .cnect-loader-link-inverted .cnect-loader-link-ring-b {
        stroke: rgba(var(--color-white), 0.55);
      }

      .cnect-loader-link-mark {
        display: inline-block;
        overflow: visible;
      }
      .cnect-loader-link-ring {
        transform-box: view-box;
      }
      .cnect-loader-link-ring-a {
        transform-origin: 25px 32px;
        animation: cnect-link-loop-a 1.5s cubic-bezier(0.62, 0.03, 0.35, 1) infinite;
      }
      .cnect-loader-link-ring-b {
        transform-origin: 39px 32px;
        animation: cnect-link-loop-b 1.5s cubic-bezier(0.62, 0.03, 0.35, 1) infinite;
      }

      @keyframes cnect-link-loop-a {
        0% {
          transform: rotate(0deg);
          stroke-dashoffset: 0;
        }
        45% {
          transform: rotate(190deg);
        }
        100% {
          transform: rotate(360deg);
          stroke-dashoffset: -82;
        }
      }
      @keyframes cnect-link-loop-b {
        0% {
          transform: rotate(0deg);
          stroke-dashoffset: 0;
        }
        45% {
          transform: rotate(-190deg);
        }
        100% {
          transform: rotate(-360deg);
          stroke-dashoffset: 82;
        }
      }

      .cnect-loader-link-title {
        animation: cnect-link-loop-title-in 1s ease-in forwards;
      }
      @keyframes cnect-link-loop-title-in {
        0% {
          opacity: 0.1;
        }
        100% {
          opacity: 1;
        }
      }

      /* Keep feedback, drop the spin. */
      @media (prefers-reduced-motion: reduce) {
        .cnect-loader-link-ring-a,
        .cnect-loader-link-ring-b {
          animation: cnect-link-loop-breathe 2.4s ease-in-out infinite;
          transform: none;
        }
        .cnect-loader-link-title {
          animation: none;
        }
      }
      @keyframes cnect-link-loop-breathe {
        0%,
        100% {
          opacity: 0.35;
        }
        50% {
          opacity: 1;
        }
      }
    `,
  ],
})
export class LoaderLinkComponent {
  /**
   * Legacy passthrough from `e11-loader-dots`, where this was a background class. The Link Loop
   * always draws itself in the theme's primary-accent + secondary pair, so the only value that
   * still changes anything is white, which flips the mark to its inverted (on-dark) treatment.
   */
  @Input() set color(value: string | undefined) {
    this.inverted = !!value && WHITE_COLORS.includes(value)
  }

  /** Draw the mark in white, for loaders on a dark surface or over a dark backdrop. */
  @Input() inverted = false

  /** Size of the mark. */
  @Input() size: LoaderLinkSize = 'md'

  /** Font size of the label. */
  @Input() fontSize = '1rem'

  /** Font color class for the label, e.g. 'e11-text-skin-base', 'e11-text-white'. */
  @Input() fontColor = 'e11-text-skin-base'

  /** Text shown beneath the mark. */
  @Input() label = ''

  /** Render inline inside a button - hides the label and drops the outer spacing. */
  @Input() buttonLoader = false

  /** Fade the loader out rather than removing it outright. */
  @Input() fade = false

  /** Center the loader over the whole viewport. */
  @Input() fullScreen = false

  /** Whether the loader is shown at all. */
  @Input() active = true

  /** Dim the page behind a full screen loader. */
  @Input() hasBackdrop = false

  /** Backdrop tint. */
  @Input() backdropStyle: 'dark' | 'light' = 'dark'

  get dimensions(): LinkLoopDimensions {
    return DIMENSIONS[this.size] ?? DIMENSIONS.md
  }
}
